import React, { useEffect, useRef, useState, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Row, Col, Card, Button, Typography, Form, notification, Modal, Divider, Select, Switch } from "antd";
import { PlusOutlined, DeleteFilled, LockOutlined } from "@ant-design/icons";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment-timezone";
import metaData from "../../../data/metaData";
import _ from "lodash";
import { showErrMsg } from "../../../utils/error";
import { formatProductWithAvailableQty, getPeriodStatus } from "../../../utils/estamp";
import dayjs from "dayjs";

//
import SectionHeader from "../../../components/GetStarted/SectionHeader";
import GiftTable from "../../../components/Estamp/GiftTable";
import GiftDetailDrawer from "../../../components/Estamp/GiftDetailDrawer";
import ItemSelectionDrawer from "../../../components/ItemSelectionDrawer";
import useAuthStore from "../../../states/useAuth";
import useEstamp from "../../../hooks/useEstamp";
import useShop from "../../../hooks/useShop";
import PeriodDetailDrawer from "../../../components/Estamp/PeriodDetailDrawer";
import { formatTime } from "../../../utils/time";
import useProductQuery from "../../../hooks/useQuery/useProduct";

const { Title, Text, Paragraph } = Typography;

const getPeriodStatusButtonStyle = (status) => {
  switch (status) {
    case "earnEnded":
      return {
        color: `var(--ion-color-step-600)`,
        background: `var(--ion-color-step-100)`,
      };
    case "ongoing":
      return {
        color: `var(--ion-color-green-600)`,
        background: `var(--ion-color-green-100)`,
      };
    case "pending":
      return {
        color: `var(--ion-color-orange-600)`,
        background: `var(--ion-color-orange-100)`,
      };
    default:
      return {
        color: `var(--ion-color-step-600)`,
        background: `var(--ion-color-step-100)`,
      };
  }
};

const Estamp = () => {
  const history = useHistory();
  const location = useLocation();

  const { user } = useAuthStore();
  const { getAllianceShop } = useShop();
  const { getSingleEstamp, updateEstamp } = useEstamp();

  //
  const [estampForm] = Form.useForm();

  //
  const [originalEstamp, setOriginalEstamp] = useState();
  const [estamp, setEstamp] = useState();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [isFetchingEstamp, setIsFetchingEstamp] = useState(false);
  const [isShowErrorPage, setIsShowErrorPage] = useState(false);

  //
  const [allianceShops, setAllianceShops] = useState([]);
  const [isFetchingAllianceShop, setIsFetchingAllianceShop] = useState(false);

  //
  const [isShowPeriodDrawer, setIsShowPeriodDrawer] = useState(false);
  const [isShowShopDrawer, setIsShowShopDrawer] = useState(false);
  const [isShowGiftDrawer, setIsShowGiftDrawer] = useState(false);
  const [isShowGiftDetailDrawer, setIsShowGiftDetailDrawer] = useState(false);

  // log.debug(estamp);

  const getAllianceShopData = async () => {
    if (!user?.companyId) {
      return;
    }

    try {
      let result;
      setIsFetchingAllianceShop(true);

      result = await getAllianceShop(user.companyId, false, false);
      // log.debug(result);

      //add key to disable antd table warning
      setAllianceShops(
        result.map((e, i) => {
          return { ...e, key: i };
        })
      );
    } catch (error) {
      log.debug(error);

      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    } finally {
      setIsFetchingAllianceShop(false);
    }
  };

  //
  const {
    data: products = [],
    isFetching: isFetchingProduct,
    refetch: refetchProduct,
  } = useProductQuery({ type: "voucher", companyId: user?.companyId });
  const filteredProducts = useMemo(() => {
    // Map products to add qty.available using the utility function
    return products.map((product, i) => {
      // Create an array of gifts that match the current product id
      const relevantGifts = estamp?.gifts?.filter((gift) => gift.productId === product.id) || [];

      return formatProductWithAvailableQty(product, relevantGifts); // Utilize the utility function
    });
    // .filter((product) => product.qty.available > 0); // Filter to keep products with qty.available > 0
  }, [products, estamp?.gifts]);

  log.debug(filteredProducts);

  const getEstampData = async () => {
    if (!user?.companyId) {
      return;
    }

    let queryURL = location.search || undefined;
    log.debug(queryURL);
    const queryParams = c.parseQueryToJSON(queryURL);
    const { pagination } = queryParams;
    log.debug(pagination);

    let result;

    try {
      setIsFetchingEstamp(true);
      result = await getSingleEstamp(user?.companyId, false, false);
      log.debug(result);

      if (result?.gifts) {
        // Sort gifts by display order
        result.gifts.sort((a, b) => a.displayOrder - b.displayOrder);

        // Group gifts by productId
        const giftsByProductId = result.gifts.reduce((acc, gift) => {
          acc[gift.productId] = acc[gift.productId] || [];
          acc[gift.productId].push(gift);
          return acc;
        }, {});

        // Update each gift's product with aggregated qty.available
        result.gifts = result.gifts.map((gift) => {
          const relevantGifts = giftsByProductId[gift.productId] || [];
          const productWithQty = formatProductWithAvailableQty(gift.product, relevantGifts); // Calculate qty.available across all gifts with the same productId
          return {
            ...gift,
            product: productWithQty, // Injected qty.available into product
          };
        });
      }

      setEstamp(result);
    } catch (error) {
      setIsShowErrorPage(true);
      let message = error.response ? error.response.data : error.message;
      log.debug(message);
      if (message == "Estamp not found") {
        return;
      }

      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    } finally {
      setIsFetchingEstamp(false);
    }
  };

  const updateEstampData = (newData) => {
    setEstamp((prev) => {
      const updated = _.merge({}, prev, newData);
      log.debug(prev);
      log.debug(newData);

      // Explicitly overwrite certain fields instead of merging
      if ("period" in newData) {
        updated.period = newData.period;
      }

      if ("shopIds" in newData) {
        updated.shopIds = newData.shopIds;
      }

      if ("gifts" in newData) {
        updated.gifts = newData.gifts;
      }

      if ("settings" in newData) {
        updated.settings = newData.settings;
      }

      log.debug(updated);
      return updated;
    });
  };

  const _updateEstamp = async (updateField, value) => {
    try {
      if (!user?.companyId) {
        return;
      }

      // Submit via axios
      setIsLoadingSubmit(true);

      // Prepare data
      let data;
      if (updateField === "shopIds") {
        data = { [updateField]: estamp[updateField] };
      } else {
        data = { [updateField]: value };
      }

      //remove populated data
      for (let gift of data?.gifts || []) {
        delete gift.product;
      }

      log.debug(data);

      let response = await updateEstamp(user.companyId, data);

      // Close drawer
      setIsShowPeriodDrawer(false);
      setIsShowShopDrawer(false);
      setIsShowGiftDetailDrawer(false);

      // Update state and notify success
      setEstamp(response);
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Updated
          </Title>
        ),
      });

      // Get populated data again since return data is not populated
      getEstampData();
      refetchProduct();
      getAllianceShopData();
    } catch (error) {
      // Check for insufficient quantity error
      let message = error.response ? error.response.data : error.message;
      if (message.includes("Insufficient available quantity")) {
        // Show specific error message for insufficient quantity
        getEstampData();
        refetchProduct();

        notification.error({
          message: (
            <Title style={{ margin: 0 }} level={4}>
              Insufficient Quantity
            </Title>
          ),
          description: <Text>Inventory has changed, please adjust qty and retry.</Text>,
        });
      } else {
        // Generic error message for other errors
        showErrMsg(error);
      }
    } finally {
      setIsLoadingSubmit(false); // Ensure loading state is cleared
    }
  };

  useEffect(() => {
    getEstampData();
    getAllianceShopData();
  }, []);

  return (
    <div style={{ margin: "0 auto" }}>
      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        <Col span={16}>
          <Period
            state={{
              data: estamp?.periods,
              form: estampForm,
              onChange: updateEstampData,
            }}
            gifts={estamp?.gifts}
            isFetchingEstamp={isFetchingEstamp}
            isShowPeriodDrawer={isShowPeriodDrawer}
            setIsShowPeriodDrawer={setIsShowPeriodDrawer}
            isLoadingSubmit={isLoadingSubmit}
            onSave={(updated) => {
              _updateEstamp("periods", updated);
            }}
          />
        </Col>
        <Col span={8}>
          <Shop
            state={{
              data: estamp?.shopIds,
              form: estampForm,
              onChange: updateEstampData,
            }}
            isShowShopDrawer={isShowShopDrawer}
            setIsShowShopDrawer={setIsShowShopDrawer}
            isFetchingAllianceShop={isFetchingAllianceShop}
            allianceShops={allianceShops}
            isLoadingSubmit={isLoadingSubmit}
            onSave={() => {
              _updateEstamp("shopIds");
            }}
          />
          <Settings
            state={{
              data: estamp?.settings,
              form: estampForm,
            }}
            isLoadingSubmit={isLoadingSubmit}
            onSave={(updated) => {
              _updateEstamp("settings", updated);
            }}
          />
        </Col>

        <Col span={24}>
          <Gift
            state={{
              data: estamp?.gifts,
              form: estampForm,
              onChange: updateEstampData,
            }}
            periods={estamp?.periods}
            isFetchingProduct={isFetchingProduct}
            isShowGiftDrawer={isShowGiftDrawer}
            setIsShowGiftDrawer={setIsShowGiftDrawer}
            isShowGiftDetailDrawer={isShowGiftDetailDrawer}
            setIsShowGiftDetailDrawer={setIsShowGiftDetailDrawer}
            products={filteredProducts}
            isLoadingSubmit={isLoadingSubmit}
            onSave={(updated) => {
              _updateEstamp("gifts", updated);
            }}
          />
        </Col>
      </Row>
    </div>
  );
};

const Period = ({
  state = {},
  gifts = [],
  isFetchingEstamp,
  isShowPeriodDrawer,
  setIsShowPeriodDrawer,
  isLoadingSubmit,
  onSave,
}) => {
  const { data = [], form, onChange } = state;
  const [period, setPeriod] = useState();

  const addNewPeriodCard = () => (
    <Col span={12}>
      <Row
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "150px",
          // border: "1px dashed #d9d9d9",
          cursor: "pointer",
          background: "var(--ion-color-step-50)",
        }}
        onClick={() => {
          if (isFetchingEstamp) {
            return;
          }

          const defaultPeriod = {
            name: undefined,
            rate: 0,
            time: undefined,
          };

          setPeriod(defaultPeriod);
          setIsShowPeriodDrawer(true);
        }}
      >
        <Button size="large" type="text" icon={<PlusOutlined />} loading={isFetchingEstamp}>
          Add Coin Period
        </Button>
      </Row>
    </Col>
  );

  return (
    <>
      <PeriodDetailDrawer
        open={isShowPeriodDrawer}
        isLoading={isLoadingSubmit}
        period={period}
        periods={data}
        gifts={gifts}
        header={
          <SectionHeader
            title="Coin Period Detail"
            description={"Provide detail of the coin"}
            style={{ margin: "0 0 0 1rem" }}
          />
        }
        confirmBtnText="Submit"
        canDelete
        onClose={() => {
          setPeriod();
          setIsShowPeriodDrawer(false);
        }}
        onDelete={(periodId) => {
          let updated = data?.filter((period) => period.id !== periodId);
          onSave(updated);
        }}
        onConfirm={(period) => {
          const existingIndex = data?.findIndex((item) => item.id === period.id);

          let updated = [...data];
          if (existingIndex !== -1) {
            // Replace the existing element
            updated[existingIndex] = period;
          } else {
            // If it doesn't exist, add the new period to the end of the array
            updated.push(period);
          }

          onSave(updated);
        }}
      />
      <Card
        title={<SectionHeader title={"Coin Period"} description={"Setup coin with expiry date, earning period, etc"} />}
        // extra={
        //   <Button
        //     size="large"
        //     type="primary"
        //     loading={isFetchingEstamp}
        //     disabled={data?.length === 2}
        //     icon={<PlusOutlined />}
        //     onClick={() => {
        //       const defaultPeriod = {
        //         name: undefined,
        //         rate: 0,
        //         time: undefined,
        //       };

        //       setPeriod(defaultPeriod);
        //       setIsShowPeriodDrawer(true);
        //     }}
        //   >
        //     Add Coin
        //   </Button>
        // }
      >
        <Row gutter={[16, 16]} style={{ alignItems: "stretch" }}>
          {data?.length === 0 ? (
            // Show centered "+ Add Period" button when there are no periods
            <Row justify="center" style={{ width: "100%" }}>
              {addNewPeriodCard()}
            </Row>
          ) : (
            <>
              {/* Render the existing periods */}
              {data.map((period, index) => (
                <Col span={12} key={period.id || index} style={{ display: "flex" }}>
                  <Card
                    hoverable={getPeriodStatus(period) === "pending"}
                    style={{
                      flex: 1, // Make sure the card takes full available height
                      display: "flex",
                      flexDirection: "column",
                      borderLeft: `5px solid var(--ion-color-${
                        ["ongoing", "earnEnded"].includes(getPeriodStatus(period)) ? "green-600" : "orange-600"
                      })`,
                    }}
                    onClick={() => {
                      if (getPeriodStatus(period) === "pending") {
                        setPeriod(period); // Handle click for ongoing period
                        setIsShowPeriodDrawer(true);
                      }
                    }}
                  >
                    <Row justify="space-between">
                      <Col span={18}>
                        <Paragraph
                          style={{ width: "100%", height: "5rem", whiteSpace: "pre-wrap" }}
                          ellipsis={{
                            rows: 2,
                            expandable: false,
                          }}
                        >
                          <Title level={4}>{period?.name}</Title>
                        </Paragraph>
                      </Col>
                      {getPeriodStatus(period) === "ongoing" ? (
                        <Col>
                          <Text strong style={{ marginTop: "1rem", color: "var(--ion-color-step-600)" }}>
                            <LockOutlined /> read only
                          </Text>
                        </Col>
                      ) : null}
                    </Row>

                    <Row align="middle">
                      <Col style={{ marginRight: "1rem" }}>
                        <Row>Earning period:</Row>
                        <Row>
                          {period?.time?.earn
                            ? `${formatTime(period.time.earn.start, "YYYY-MM-DD")} to ${formatTime(
                                period.time.earn.end,
                                "YYYY-MM-DD"
                              )}`
                            : "N/A"}
                        </Row>
                      </Col>
                      <Col>
                        <Button
                          type="text"
                          style={{
                            fontSize: "1rem",
                            marginRight: "0.5rem",
                            ...getPeriodStatusButtonStyle(getPeriodStatus(period)), // Apply the styles based on status
                          }}
                        >
                          {getPeriodStatus(period) === "earnEnded" ? "ended" : getPeriodStatus(period)}
                        </Button>
                      </Col>
                    </Row>

                    <Divider />

                    <Row align="middle">
                      <Col style={{ marginRight: "1rem" }}>
                        <Row>Coin expiry at:</Row>
                        <Row>{period?.time?.expiry ? formatTime(period.time.expiry, "YYYY-MM-DD") : "N/A"}</Row>
                      </Col>
                      <Col>
                        <Button
                          type="text"
                          style={{
                            fontSize: "1rem",
                            color: `var(--ion-color-${
                              ["ongoing", "earnEnded"].includes(getPeriodStatus(period)) ? "green-600" : "orange-600"
                            })`,
                            background: `var(--ion-color-${
                              ["ongoing", "earnEnded"].includes(getPeriodStatus(period)) ? "green-100" : "orange-100"
                            })`,
                          }}
                        >
                          {getPeriodStatus(period) === "pending" ? "pending" : "ongoing"}
                        </Button>
                      </Col>
                    </Row>

                    <Row style={{ marginTop: "1rem" }}>
                      <Title level={5}>{`$${period?.rate} Earn 1 Coin`}</Title>
                    </Row>
                  </Card>
                </Col>
              ))}
              {/* Show "+ Add Period" card when there is one period */}
              {data.length === 1 && addNewPeriodCard()}
            </>
          )}
        </Row>
      </Card>
    </>
  );
};

const Shop = ({
  state = {},
  isShowShopDrawer,
  setIsShowShopDrawer,
  isFetchingAllianceShop,
  allianceShops,
  isLoadingSubmit,
  onSave,
}) => {
  const { data = [], form, onChange } = state;

  return (
    <>
      <ItemSelectionDrawer
        state={{
          data: data,
          form: form,
          onChange: (shopIds, shops) => {
            onChange({
              shopIds: shopIds,
              shops: shops,
            });
          },
        }}
        header={
          <SectionHeader
            title="Shops - Earn Coin"
            description={"select shops that can earn Coin"}
            style={{ margin: "0 0 0 1rem" }}
          />
        }
        items={allianceShops}
        isShow={isShowShopDrawer}
        isLoading={isLoadingSubmit}
        closable={false}
        tableType="shop"
        confirmBtnText={"Save"}
        onConfirm={onSave}
      />

      <Card
        title={<SectionHeader title={"Shops - Earn Coin"} description={"List of shops that can earn Coin"} />}
        style={{}}
        extra={
          <Button
            size="large"
            type="primary"
            loading={isFetchingAllianceShop}
            onClick={() => {
              setIsShowShopDrawer(true);
            }}
          >
            Manage
          </Button>
        }
      >
        <Row align="middle">
          Total
          <Title level={4} style={{ margin: "0 0.5rem" }}>
            {data?.length > 0 ? data.length : 0}
          </Title>
          Shops joined
        </Row>
      </Card>
    </>
  );
};

const Settings = ({ state = {}, isLoadingSubmit, onSave }) => {
  const { data = [], form, onChange } = state;

  return (
    <>
      <Card
        title={<SectionHeader title={"Setting"} description={"Control Pay 2 Earn feature"} />}
        style={{ marginTop: "1rem" }}
      >
        <Row style={{ width: "100%" }}>
          <Row justify="space-between" align="middle" style={{ width: "100%" }}>
            <Col>Pay 2 Earn</Col>
            <Col>
              <Switch
                checkedChildren="On"
                unCheckedChildren="Off"
                loading={isLoadingSubmit}
                checked={data?.isPayToEarnEnabled}
                onChange={(checked) => {
                  onSave({ isPayToEarnEnabled: checked });
                }}
                style={{
                  transform: "scale(1.25)", // Increase the size
                }}
              />
            </Col>
          </Row>
        </Row>
      </Card>
    </>
  );
};

const Gift = ({
  state = {},
  periods = [],
  isFetchingProduct,
  isShowGiftDrawer,
  setIsShowGiftDrawer,
  isShowGiftDetailDrawer,
  setIsShowGiftDetailDrawer,
  products,
  isLoadingSubmit,
  onSave,
}) => {
  const { data = [], form } = state;

  // Local state to manage selected product IDs
  const [selectedProductIds, setSelectedProductIds] = useState(products.map((e) => e.id));
  const [gift, setGift] = useState();

  const [isShowPeriodModal, setIsShowPeriodModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(); // Store the selected period object
  const [filteredProducts, setFilteredProducts] = useState(products); // State for filtered products

  const product = useMemo(() => {
    return selectedProductIds.length > 0 ? products.find((e) => e.id == selectedProductIds[0]) : undefined;
  }, [gift, selectedProductIds, products]);

  // Filter products based on the selected period
  useEffect(() => {
    let filtered = products.filter((e) => e.qty.available > 0);

    if (selectedPeriod) {
      const start = dayjs(selectedPeriod.time.earn.start);
      const end = dayjs(selectedPeriod.time.expiry);

      filtered = filtered.filter((product) => {
        const redeemStart = dayjs(product.time.redeem.start);
        const redeemEnd = dayjs(product.time.redeem.end);

        // Keep the product if redeem period overlaps with selected period (inclusive)
        return !(redeemStart.isAfter(end) || redeemEnd.isBefore(start));
      });
    }
    //
    setFilteredProducts(filtered);
  }, [selectedPeriod, products]);

  const onSelectChange = (productIds) => {
    setSelectedProductIds(productIds); // Update local state
  };

  const handleAddGift = () => {
    form.resetFields(["period"]); // Reset the 'period' field specifically
    setSelectedPeriod();
    setIsShowPeriodModal(true);
  };

  const handlePeriodChange = (value) => {
    const selected = periods.find((period) => period.id === value);
    setSelectedPeriod(selected); // Save the selected period object
  };

  const handlePeriodSubmit = () => {
    if (!selectedPeriod) {
      return notification.error({ message: "please select coin" });
    }
    setIsShowPeriodModal(false);
    setIsShowGiftDrawer(true);
  };

  const onConfirm = () => {
    const defaultGift = {
      productId: selectedProductIds[0],
      qty: 0,
      cost: 0,
      time: undefined,
    };

    setGift(defaultGift);
    setIsShowGiftDrawer(false);
    setIsShowGiftDetailDrawer(true);
  };

  useEffect(() => {
    if (isShowGiftDrawer) {
      setSelectedProductIds([]); // Reset the selection when the drawer opens
    }
  }, [isShowGiftDrawer]);

  return (
    <>
      <Modal
        title="Select Coin"
        centered
        open={isShowPeriodModal}
        closable={false}
        onCancel={() => {
          setIsShowPeriodModal(false);
        }}
        onOk={handlePeriodSubmit}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="period" rules={[{ required: true, message: "required" }]}>
            <Select
              size="large"
              style={{ width: "100%" }}
              placeholder="Select coin"
              value={selectedPeriod?.id} // Set the value of Select to the id of the selected period
              options={periods.map((period) => ({
                label: `${period.name} (${formatTime(period.time.earn.start, "YYYY-MM-DD")} - ${formatTime(
                  period.time.earn.end,
                  "YYYY-MM-DD"
                )})`,
                value: period.id,
              }))}
              onChange={handlePeriodChange}
            />
          </Form.Item>
        </Form>
      </Modal>

      <ItemSelectionDrawer
        state={{
          data: selectedProductIds,
          form: form,
          onChange: onSelectChange,
        }}
        header={
          <SectionHeader
            title="Select Gift"
            description={"Only voucher can be selected as gift"}
            style={{ margin: "0 0 0 1rem" }}
          />
        }
        items={filteredProducts} // Use filtered products instead of all products
        isShow={isShowGiftDrawer}
        isLoading={isLoadingSubmit}
        isSingleSelect={true}
        tableType="product"
        confirmBtnText={"Next"}
        isConfirmBtnDisabled={selectedProductIds.length === 0}
        onClose={() => {
          setIsShowGiftDrawer(false);
        }}
        onConfirm={onConfirm}
      />

      <GiftDetailDrawer
        open={isShowGiftDetailDrawer}
        isLoading={isLoadingSubmit}
        gift={gift}
        product={product}
        gifts={data}
        selectedPeriod={selectedPeriod}
        header={
          <SectionHeader
            title="Gift Detail"
            description={"Provide detail of the gift"}
            style={{ margin: "0 0 0 1rem" }}
          />
        }
        confirmBtnText="Submit"
        onClose={() => {
          setGift();
          setIsShowGiftDetailDrawer(false);
        }}
        onConfirm={(gift) => {
          const existingIndex = data?.findIndex((item) => item.id === gift.id);
          let updated = [...(data || [])];
          if (existingIndex !== -1) {
            updated[existingIndex] = gift; // Replace the existing element
          } else {
            updated.push(gift); // If it doesn't exist, add the new gift to the end of the array
          }

          onSave(updated);
        }}
      />

      <Card
        title={<SectionHeader title={"Gifts"} description={"List of gifts that customer can redeem using Coin"} />}
        style={{ marginBottom: "2rem" }}
        extra={
          <Button
            size="large"
            type="primary"
            loading={isFetchingProduct}
            disabled={periods.length == 0}
            icon={<PlusOutlined />}
            onClick={handleAddGift} // Call the add gift handler
          >
            Add Gift
          </Button>
        }
      >
        <GiftTable
          state={{
            data: data,
          }}
          periods={periods}
          isLoading={isLoadingSubmit}
          onEdit={(giftId) => {
            let gift = data.find((e) => e.id == giftId);
            let period = periods.find((e) => e.id == gift.periodId);
            setGift(gift);
            setSelectedProductIds([gift?.productId]);
            setSelectedPeriod(period);
            setIsShowGiftDetailDrawer(true);
          }}
          onDelete={(giftId) => {
            let updated = data?.filter((gift) => gift.id !== giftId);
            onSave(updated);
          }}
          onUpdate={(updated) => {
            onSave(updated);
          }}
        />
      </Card>
    </>
  );
};

export default Estamp;
